import {
    Component,
    EventEmitter,
    Input,
    Output,
    OnChanges,
    SimpleChanges,
    inject,
} from '@angular/core';
import { AuthService } from '@services';
import {
    DxButtonModule,
    DxDropDownButtonModule,
    DxToolbarModule,
} from 'devextreme-angular';
import {
    PermissionType,
    AccessType,
    AppointmentAction,
} from '@services/web-api-client';

interface MenuAction {
    id: AppointmentAction;
    text: string;
    icon: string;
}

@Component({
    selector: 'app-appointment-toolbar',
    standalone: true,
    imports: [DxToolbarModule, DxButtonModule, DxDropDownButtonModule],
    templateUrl: './appointment-toolbar.component.html',
})
export class AppointmentToolbarComponent implements OnChanges {
    @Input() appointmentNumber: string | null = null;
    @Input() appointmentActions: AppointmentAction[] | null = null;
    @Input() isReadOnly: boolean = false;
    @Input() isSubmitting: boolean = false;
    @Input() isLoading: boolean = false;

    @Output() cancelClick = new EventEmitter<void>();
    @Output() shuntMoveClick = new EventEmitter<void>();
    @Output() assignToUnloadClick = new EventEmitter<void>();
    @Output() completeClick = new EventEmitter<void>();
    @Output() save = new EventEmitter<AppointmentAction>();

    protected auth = inject(AuthService);
    protected moreMenuActions: MenuAction[] = [];
    public AppointmentAction = AppointmentAction;

    private readonly allMenuActions: MenuAction[] = [
        {
            id: AppointmentAction.SendBack,
            text: 'Send Back',
            icon: 'back',
        },
        {
            id: AppointmentAction.NoShow,
            text: 'No Show',
            icon: 'close',
        },
        {
            id: AppointmentAction.Decline,
            text: 'Decline',
            icon: 'close',
        },
        {
            id: AppointmentAction.ShuntMovePending,
            text: 'Shunt Move',
            icon: 'cursormove',
        },
        {
            id: AppointmentAction.AssignToUnload,
            text: 'Assign to Unload',
            icon: 'user',
        },
        {
            id: AppointmentAction.RequestTags,
            text: 'Request Tags',
            icon: 'doc',
        },
        {
            id: AppointmentAction.Investigate,
            text: 'Investigate',
            icon: 'info',
        },
        {
            id: AppointmentAction.RefuseLoad,
            text: 'Refuse Load',
            icon: 'close',
        },
        {
            id: AppointmentAction.CancelMove,
            text: 'Cancel Move',
            icon: 'close',
        },
        {
            id: AppointmentAction.CheckOut,
            text: 'Check Out',
            icon: 'fa-light fa-right-from-bracket',
        },
    ];

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['appointmentStatus'] || changes['appointmentActions']) {
            this.buildMenuActions();
        }
    }

    private buildMenuActions(): void {
        if (this.isLoading) {
            this.moreMenuActions = [];
            return;
        }

        this.moreMenuActions = this.allMenuActions.filter((action) =>
            this.canShowButton(action.id),
        );
    }

    canShowButton(action: AppointmentAction): boolean {
        if (this.isLoading) return false;

        if (!this.appointmentNumber && !this.isReadOnly) {
            return (
                (action === AppointmentAction.Submit &&
                    this.auth.checkPermission(PermissionType.AppointmentSubmit, AccessType.Manage)) || 
                    action === AppointmentAction.SaveAsDraft
            );
        }

        if (this.appointmentNumber && !this.isReadOnly && action === AppointmentAction.Save) {
            return true;
        }

        return this.appointmentActions?.includes(action) ?? false;
    }

    get showMoreMenu(): boolean {
        return this.appointmentNumber !== null && this.moreMenuActions.length > 0;
    }

    onMenuActionClick(e: { itemData?: MenuAction }): void {
        if (!e.itemData) return;

        switch (e.itemData.id) {
            case AppointmentAction.AssignToUnload:
                this.assignToUnloadClick.emit();
                break;
            case AppointmentAction.ShuntMovePending:
                this.shuntMoveClick.emit();
                break;
            default:
                this.onButtonClick(e.itemData.id as AppointmentAction);
        }
    }

    onButtonClick(status: AppointmentAction) {
        this.save.emit(status);
    }

    onCompleteClick() {
        this.completeClick.emit();
    }
}
